import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { FaPlaneDeparture, FaPlaneArrival, FaSuitcase, FaSuitcaseRolling } from 'react-icons/fa';
import {
  PackageFlightLeg,
  formatPackageFlightRange,
  packageLegAirlineImage,
  packageLegAirlineName,
  packageLegRoute,
} from './packageFlightUtils';

interface PackageFlightsInfoProps {
  /** ALP package `flights` object: { flightFrom: [...], flightBack: [...] }. */
  flights?: { flightFrom?: PackageFlightLeg[]; flightBack?: PackageFlightLeg[] } | null;
  /** Compact = single-line legs (search card); full = boxed section (details). */
  compact?: boolean;
}

/**
 * Included-flights block for package cards — the bot-widget counterpart of
 * FlightsSet2/FlightBox: airline, flight code, route, times, duration, baggage.
 */
const PackageFlightsInfo: React.FC<PackageFlightsInfoProps> = ({ flights, compact = false }) => {
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const out = flights?.flightFrom?.[0];
  const back = flights?.flightBack?.[flights?.flightBack?.length - 1 ?? 0];
  if (!out && !back) return null;

  const renderLeg = (leg: PackageFlightLeg | undefined, isReturn: boolean) => {
    if (!leg) return null;
    const airline = packageLegAirlineName(leg);
    const logo = packageLegAirlineImage(leg);
    const route = packageLegRoute(leg);
    const times = formatPackageFlightRange(leg.departure_date, leg.arrival_date);
    const bag = leg.baggage;
    return (
      <div className="bot-package-flight-leg" key={isReturn ? 'back' : 'out'}>
        <span className="bot-package-flight-leg__dir">
          {isReturn ? <FaPlaneArrival /> : <FaPlaneDeparture />}
          {!compact && (
            <span className="bot-package-flight-leg__label">
              {isReturn ? t?.result?.return ?? 'Return' : t?.result?.outbound ?? 'Outbound'}
            </span>
          )}
        </span>
        {logo ? (
          <img src={logo} alt={airline ?? ''} className="bot-package-flight-leg__logo" />
        ) : null}
        <span className="bot-package-flight-leg__main">
          {[
            [airline, leg.flight_number].filter(Boolean).join(' '),
            route,
            times,
            leg.duration,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {!compact && bag && (
          <span className="bot-package-flight-leg__baggage">
            <FaSuitcase
              title={bag.is_include_suitcase ? t?.result?.suitcaseIncluded ?? 'Suitcase included' : t?.result?.noSuitcase ?? 'No suitcase'}
              className={bag.is_include_suitcase ? '' : 'bot-package-flight-leg__baggage--off'}
            />
            <FaSuitcaseRolling
              title={bag.is_include_trolly ? t?.result?.trolleyIncluded ?? 'Trolley included' : t?.result?.noTrolley ?? 'No trolley'}
              className={bag.is_include_trolly ? '' : 'bot-package-flight-leg__baggage--off'}
            />
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={`bot-package-flights${compact ? ' bot-package-flights--compact' : ''}`}>
      {!compact && (
        <div className="bot-package-flights__title">
          {t?.result?.flightsIncluded ?? 'Flights included'}
        </div>
      )}
      {renderLeg(out, false)}
      {renderLeg(back, true)}
    </div>
  );
};

export default PackageFlightsInfo;
